"use client";

import React, { useEffect, useState } from "react";

interface GlitchTextProps {
  text: string;
  glitchInterval?: number;
  glitchDuration?: number;
  className?: string;
}

export const GlitchText: React.FC<GlitchTextProps> = ({
  text,
  glitchInterval = 30,
  glitchDuration = 3000,
  className = "",
}) => {
  const [glitchedText, setGlitchedText] = useState(text);
  const [isGlitching, setIsGlitching] = useState(false);

  useEffect(() => {
    setIsGlitching(true);

    const glitchChars = "!<>-_\\/[]{}—=+*^?#________";
    let intervalId: NodeJS.Timeout;
    let elapsedTime = 0;

    const glitchText = () => {
      return text
        .split("")
        .map((char) => {
          if (Math.random() < 0.1) {
            return glitchChars[Math.floor(Math.random() * glitchChars.length)];
          }
          return char;
        })
        .join("");
    };

    const applyGlitchEffect = () => {
      intervalId = setInterval(() => {
        setGlitchedText(glitchText());

        elapsedTime += glitchInterval;
        if (elapsedTime >= glitchDuration) {
          clearInterval(intervalId);
          setGlitchedText(text);
          setIsGlitching(false);
        }
      }, glitchInterval);
    };

    applyGlitchEffect();

    return () => {
      clearInterval(intervalId);
    };
  }, [text, glitchInterval, glitchDuration]);

  if (!isGlitching) {
    return <span className={className}>{text}</span>;
  }

  return (
    <div className={`relative inline-block ${className}`}>
      <span className="relative z-10">{glitchedText}</span>
      <span
        className="absolute inset-0 z-0"
        style={{
          textShadow: "1px 0 #ff00ea, -1px 0 #00ff00",
          animation: "glitch 0.5s infinite",
          opacity: 0.5,
        }}
      >
        {glitchedText}
      </span>
      <style jsx>{`
        @keyframes glitch {
          0% {
            transform: translate(0);
          }
          20% {
            transform: translate(-2px, 2px);
          }
          40% {
            transform: translate(-2px, -2px);
          }
          60% {
            transform: translate(2px, 2px);
          }
          80% {
            transform: translate(2px, -2px);
          }
          100% {
            transform: translate(0);
          }
        }
      `}</style>
    </div>
  );
};
